import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../services/api';
import AptitudeAnalytics from '../components/aptitude/AptitudeAnalytics';
import { AptitudeHighlights } from '../components/aptitude/AptitudeTimeline';
import DiagnosticIntelligenceSection from '../components/diagnostic/DiagnosticIntelligenceSection';
import { SkeletonCard } from '../components/ui/Skeleton';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import PlayerJourneyHero from '../components/player/PlayerJourneyHero';
import SkillMap from '../components/player/SkillMap';
import AchievementsPanel from '../components/player/AchievementsPanel';
import MilestoneCard from '../components/player/MilestoneCard';
import PersonalRecordsGrid from '../components/player/PersonalRecordsGrid';
import CognitiveSnapshot from '../components/player/CognitiveSnapshot';
import { useStudent } from '../components/student/StudentContext';
import './StudentProgress.css';

// Progress is a JOURNEY, not another analytics dashboard. Fixed reading order: Level → Cognitive snapshot →
// Achievements → Continuous performance analytics → Milestones → Highlights → Personal bests → skill map,
// recovered mistakes and topic diagnostics. Nothing was removed; the detailed analytics simply sit lower.
const StudentProgress = () => {
  const location = useLocation();
  const { player } = useStudent();
  const [dashboard, setDashboard] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [progression, setProgression] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(null);
  const [showJustCompleted, setShowJustCompleted] = useState(!!location.state?.justCompleted);

  const load = () => {
    setLoading(true); setFailed(null);
    Promise.allSettled([
      API.get('/portal/dashboard'),
      API.get('/portal/diagnostic-intelligence'),
      API.get('/portal/progression'),
    ]).then(([dashRes, diagRes, progRes]) => {
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data); else setFailed(dashRes.reason);
      if (diagRes.status === 'fulfilled') setDiagnostic(diagRes.value.data);
      if (progRes.status === 'fulfilled') setProgression(progRes.value.data);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  if (loading) {
    return <div className="z-page z-page--narrow" role="status" aria-label="Loading your progress"><SkeletonCard lines={2} /><SkeletonCard lines={4} /></div>;
  }
  if (failed && !dashboard) return <div className="z-page z-page--narrow"><ErrorState error={failed} onRetry={load} /></div>;

  const aptitude = dashboard?.aptitude || {};
  const readiness = dashboard?.readiness;
  const recovered = diagnostic?.mistakes?.recently_corrected_items || [];
  const hasHistory = (aptitude.history || []).length > 0;
  const topics = diagnostic?.topics || [];

  return (
    <div className="z-page z-page--narrow progress-page-content">
      {showJustCompleted && (
        <div className="progress-just-completed" role="status">
          <span><Icon name="sparkle" size={18} /> Your latest session is reflected below.</span>
          <button type="button" aria-label="Dismiss" onClick={() => setShowJustCompleted(false)}><Icon name="close" size={18} /></button>
        </div>
      )}

      {/* 1 · Level */}
      {player && <PlayerJourneyHero progress={player} readiness={readiness} />}

      {/* 2 · Cognitive snapshot */}
      <section className="z-section" aria-labelledby="pg-cog">
        <h2 className="z-section-title" id="pg-cog">Cognitive snapshot</h2>
        <CognitiveSnapshot readiness={readiness} avgScore={dashboard?.overallStats?.avg_score} />
      </section>

      {/* 3 · Achievements */}
      {progression && (
        <section className="z-section" aria-labelledby="pg-ach">
          <h2 className="z-section-title" id="pg-ach">Achievements</h2>
          <AchievementsPanel achievements={progression.achievements} weeklyGoal={progression.weeklyGoal} groups={progression.achievementGroups} />
        </section>
      )}

      {/* 4 · Continuous performance analytics (the highlights tiles are placed separately, below) */}
      {hasHistory ? (
        <AptitudeAnalytics
          profile={aptitude.profile || null}
          history={aptitude.history || []}
          trend={aptitude.trend || []}
          subtitle="Your readiness trend across every aptitude attempt — not a one-time snapshot."
          defaultTab="timeline"
          showHighlights={false}
        />
      ) : (
        <EmptyState icon="growth" title="Your progress story starts here" body="Take your first Aptitude Test to unlock your trend." />
      )}

      {/* 5 · Milestones */}
      {progression && (
        <section className="z-section" aria-labelledby="pg-ms">
          <h2 className="z-section-title" id="pg-ms">Milestones</h2>
          <div className="progress-milestone-list">
            {progression.milestones.map(m => <MilestoneCard key={m.id} milestone={m} />)}
          </div>
        </section>
      )}

      {/* 6 · Highlights across your aptitude attempts */}
      {hasHistory && <AptitudeHighlights history={aptitude.history || []} profile={aptitude.profile || null} />}

      {/* 7 · Personal bests */}
      {progression && (
        <section className="z-section" aria-labelledby="pg-pb">
          <h2 className="z-section-title" id="pg-pb">Personal bests</h2>
          <PersonalRecordsGrid records={progression.personalRecords} />
        </section>
      )}

      {/* 8 · Everything else: skill map, recovered mistakes, topic-level diagnostics */}
      <section className="z-section" aria-labelledby="pg-skills">
        <h2 className="z-section-title" id="pg-skills">Skill map</h2>
        <SkillMap topics={topics} />
      </section>

      {recovered.length > 0 && (
        <section className="z-section" aria-labelledby="pg-rec">
          <h2 className="z-section-title" id="pg-rec">Recovered mistakes</h2>
          <div className="z-surface progress-recovered-card">
            <p className="progress-recovered-lead">
              You've corrected past mistakes in <b>{recovered.length}</b> question{recovered.length > 1 ? 's' : ''} — nice work.
            </p>
            <div className="progress-recovered-chips">
              {recovered.slice(0, 6).map((m, i) => (
                <span key={i} className="z-chip z-chip--success">{m.topic_name}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      <DiagnosticIntelligenceSection data={diagnostic} />
    </div>
  );
};

export default StudentProgress;
